import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import momentJalaali from 'moment-jalaali';
import axios from 'src/app/Service/axios';
import {
  MaturityOption,
  RawService,
  ServiceRow,
  SubjectOption,
} from './services.types';
import {
  applyMaturityRules,
  DEFAULT_MATURITY,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SUBJECT_ID,
  getStatusInfo,
  HELP_GUIDE_ID,
  MATURITY,
  PAGE_SIZE_OPTIONS,
  stripCommas,
  toEnglishDigits,
  validationError,
} from './services.utils';

declare let $: any;

@Component({
  selector: 'Services',
  templateUrl: './services.component.html',
})
export class ServicesComponent implements OnInit, OnDestroy {
  readonly loggedInUser = this.gurdService.decrypt(
    localStorage.getItem('FullName')!,
  );

  readonly vehicle = {
    owner: this.gurdService.decrypt(localStorage.getItem('carName')!),
    plate: this.gurdService
      .decrypt(localStorage.getItem('plate')!)
      .split(',')
      .join(' '),
    company: this.gurdService.decrypt(localStorage.getItem('hze')!),
  };

  readonly reversedPlate = this.vehicle.plate.split('').reverse().join('');

  unitId = '';

  rows: ServiceRow[] = [];

  filteredRows: ServiceRow[] = [];

  pagedRows: ServiceRow[] = [];

  totalPages = 1;

  searchText = '';
  currentPage = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  // ----- وضعیت UI -----
  loading = false;
  hasError = false;
  isEditMode = false;
  isExporting = false;

  showKmInput = false;
  showDateInput = false;
  showWorkHoursInput = false;

  tooltip = { text: '', visible: false, x: 0, y: 0 };

  // ----- گزینه‌های فرم -----
  maturityOptions: MaturityOption[] = [];
  subjectOptions: SubjectOption[] = [];

  // ----- فرم‌ها -----
  serviceForm: FormGroup = this.formBuilder.group({
    iRepairMaintenanceCondition: new FormControl(),
    iRepairMaintenanceSubject: new FormControl(),
    tiMaintenanceMaturity: new FormControl(),
    strComment: new FormControl(''),
    iKM: new FormControl(),
    iWorkHours: new FormControl(),
  });

  dueDateForm: FormGroup = this.formBuilder.group({
    date: new FormControl(''),
  });

  constructor(
    private notifyService: NotificationService,
    private router: Router,
    public otherService: OtherService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private location: Location,
    private gurdService: GurdServiceService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ================= چرخه‌ی حیات =================
  ngOnInit(): void {
    $('#exampleModal').on('hidden.bs.modal', this.onModalHidden);

    this.route.params.subscribe((params) => (this.unitId = params['id']));

    this.resetForm();
    this.loadServices();
    this.loadFormOptions();
  }

  ngOnDestroy(): void {
    $('#exampleModal').off('hidden.bs.modal', this.onModalHidden);
  }

  private readonly onModalHidden = () => this.resetForm();

  // ================= بارگذاری =================
  async loadServices(): Promise<void> {
    this.loading = true;
    this.hasError = false;

    try {
      const resp = await axios.post(
        `GetRepairMaintenanceCondition?strUnitID=${this.unitId}&${this.authQuery()}`,
      );
      const data: RawService[] = resp.data ?? [];

      if (data[0]?.ISSessionExpired == true) {
        this.expireSession();
        return;
      }

      // for (let index = 0; index < 10000; index++) {
      //   this.rows.push(...resp.data);
      // }
      // this.rows = this.rows.map((raw) => this.toRow(raw));

      this.rows = data.map((raw) => this.toRow(raw));
    } catch {
      this.hasError = true;
      this.rows = [];
    } finally {
      this.loading = false;
      this.refreshTable();
    }
  }

  private async loadFormOptions(): Promise<void> {
    const [maturities, subjects] = await Promise.all([
      axios.post('MaintenanceMaturity'),
      axios.post('RepairMaintenanceSubject'),
    ]);

    this.maturityOptions = maturities.data;
    this.subjectOptions = subjects.data;

    this.serviceForm.patchValue({
      iRepairMaintenanceSubject: DEFAULT_SUBJECT_ID,
      tiMaintenanceMaturity: DEFAULT_MATURITY,
    });
  }

  private toRow(raw: RawService): ServiceRow {
    const searchable = [
      raw.strUserSaved,
      raw.strRepairMaintenanceSubject_strComment,
      raw.strMaintenanceMaturity_strComment,
      raw.strDate,
      raw.iKM,
      raw.iWorkHours,
      raw.strComment,
    ];

    return {
      ...raw,
      searchIndex: searchable.map((v) => toEnglishDigits(v)).join('\n'),
      statusInfo: getStatusInfo(raw.iStatus),
      kmText: String(this.otherService.getSeparatedValue(raw.iKM) ?? ''),
    };
  }

  // ================= جستجو و صفحه‌بندی =================
  private refreshTable(resetPage = false): void {
    if (resetPage) this.currentPage = 1;

    const query = toEnglishDigits(this.searchText).trim();

    this.filteredRows = query
      ? this.rows.filter((row) => row.searchIndex.includes(query))
      : this.rows;

    this.totalPages = Math.max(
      1,
      Math.ceil(this.filteredRows.length / this.pageSize),
    );
    this.currentPage = Math.min(this.currentPage, this.totalPages);

    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  onSearchChange(): void {
    this.refreshTable(true);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.refreshTable(true);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.refreshTable();
    this.hideTooltip();
  }

  trackById(_index: number, row: ServiceRow): number {
    return row.iRepairMaintenanceCondition;
  }

  // ================= فرم و مودال =================
  startCreate(): void {
    this.resetForm();
  }

  openEditModal(row: ServiceRow): void {
    this.dueDateForm.patchValue({ date: row.strDate });

    this.serviceForm.patchValue({
      iRepairMaintenanceCondition: row.iRepairMaintenanceCondition,
      iRepairMaintenanceSubject: row.iRepairMaintenanceSubject,
      tiMaintenanceMaturity: row.tiMaintenanceMaturity,
      strComment: row.strComment,
      iWorkHours: row.iWorkHours,
      iKM: String(row.iKM ?? '').replace(/,/g, ''),
    });

    this.applyMaturityInputs(row.tiMaintenanceMaturity);
    this.isEditMode = true;
    $('#exampleModal').modal('show');
  }

  onMaturityChange(): void {
    const selectedValue = this.serviceForm.value.tiMaintenanceMaturity;
    this.applyMaturityInputs(selectedValue);
  }

  applyMaturityInputs(maturity: unknown): void {
    const type = Number(maturity);
    this.showKmInput = type === MATURITY.KM || type === MATURITY.KM_AND_DATE;
    this.showDateInput =
      type === MATURITY.DATE || type === MATURITY.KM_AND_DATE;
    this.showWorkHoursInput = type === MATURITY.WORK_HOURS;
  }

  private resetForm(): void {
    this.serviceForm.reset({
      iRepairMaintenanceSubject: DEFAULT_SUBJECT_ID,
      tiMaintenanceMaturity: DEFAULT_MATURITY,
      strComment: '',
    });
    this.dueDateForm.reset({
      date: momentJalaali().format('jYYYY/jMM/jDD'),
    });
    this.applyMaturityInputs(DEFAULT_MATURITY);
    this.isEditMode = false;
  }

  // ================= ثبت / ویرایش / حذف =================
  saveService(): void {
    const payload = this.buildPayload();
    const error = validationError(payload);

    if (error) {
      this.notifyService.showError(error);
      return;
    }

    $.confirm({
      title: 'آیا از ثبت اطلاعات اطمینان دارید ؟',
      content: '',
      type: 'green',
      buttons: {
        ok: {
          text: 'ثبت',
          btnClass: 'btn-success',
          keys: ['enter'],
          action: () => this.createService(payload),
        },
        cancel: { text: 'لغو' },
      },
    });
  }

  async updateService(): Promise<void> {
    const payload = this.buildPayload();
    const error = validationError(payload);

    if (error) {
      this.notifyService.showError(error);
      return;
    }

    if (!confirm('آیا از ویرایش اطلاعات اطمینان دارید؟')) return;

    const resp = await axios.post(
      `UpdateRepairMaintenanceCondition?${this.authQuery()}`,
      this.otherService.stringNumberToNumber(payload),
    );

    switch (Number(resp.data)) {
      case -100:
        this.expireSession(5);
        break;
      case -1:
        this.notifyService.showError('خطایی رخ داده');
        this.serviceForm.reset();
        break;
      case 1:
        this.notifyService.showSuccess('با موفقیت ثبت شد');
        this.loadServices();
        $('#exampleModal').modal('hide');
        break;
    }
  }

  async confirmDelete(row: ServiceRow): Promise<void> {
    if (!confirm('آیا از حذف اطلاعات اطمینان دارید؟')) return;

    const id = row.iRepairMaintenanceCondition;
    const resp = await axios.post(
      `DeleteRepairMaintenanceCondition?iRepairMaintenanceCondition=${id}&${this.authQuery()}`,
    );

    switch (Number(resp.data)) {
      case -100:
        this.expireSession(6);
        break;
      case 1:
        this.notifyService.showSuccess('با موفقیت حذف شد');
        this.rows = this.rows.filter(
          (r) => r.iRepairMaintenanceCondition !== id,
        );
        this.refreshTable();
        break;
      default:
        this.notifyService.showError('خطایی رخ داده');
    }
  }

  private async createService(payload: Record<string, any>): Promise<void> {
    const resp = await axios.post(
      `SaveRepairMaintenanceCondition?${this.authQuery()}`,
      this.otherService.stringNumberToNumber(payload),
    );

    switch (Number(resp.data)) {
      case -100:
        this.expireSession(4);
        break;
      case -2:
        this.notifyService.showError('این سرویس قبلا ثبت شده');
        break;
      case 1:
        this.notifyService.showSuccess('با موفقیت ثبت شد');
        this.loadServices();
        $('#exampleModal').modal('hide');
        break;
      default:
        this.notifyService.showError('خطایی رخ داده');
    }
  }

  private buildPayload(): Record<string, any> {
    const payload: Record<string, any> = {
      ...this.serviceForm.value,
      strDate: this.dueDateForm.value.date,
      strUnitId: this.unitId,
    };

    applyMaturityRules(payload);
    payload['iKM'] = stripCommas(payload['iKM']);
    payload['iWorkHours'] = stripCommas(payload['iWorkHours']);

    return payload;
  }

  // ================= چاپ و اکسل =================
  printReport(): void {
    this.renderExportTable(() => {
      const section = document.getElementById('print-table-periodicServices');
      if (!section) return;

      const styles = Array.from(
        document.querySelectorAll('link[rel="stylesheet"], style'),
      )
        .map((el) => el.outerHTML)
        .join('\n');

      const content = section.outerHTML.replace('print-only', '');

      const win = window.open('', '_blank', 'width=900,height=700');
      if (!win) {
        this.notifyService.showError('مرورگر پنجره‌ی چاپ را مسدود کرده است');
        return;
      }

      win.document.open();
      win.document.write(`
      <!doctype html>
      <html dir="rtl" lang="fa">
        <head>
          <meta charset="utf-8" />
          <title>گزارش سررسید سرویس ها</title>
          ${styles}
        </head>
        <body>${content}</body>
      </html>
    `);
      win.document.close();

      setTimeout(() => {
        win.focus();
        win.print();
        win.close();
      }, 300);
    });
  }

  exportExcel(): void {
    this.renderExportTable(() =>
      this.otherService.exportexcel(
        'excel-table-periodicServices',
        'گزارش سررسید سرویس ها.xlsx',
      ),
    );
  }

  private renderExportTable(action: () => void): void {
    this.isExporting = true;
    this.cdr.detectChanges();
    try {
      action();
    } finally {
      this.isExporting = false;
      this.cdr.detectChanges();
    }
  }

  // ================= تولتیپ و کلیدها =================
  showTooltip(event: MouseEvent, text: string): void {
    if (!text) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tooltip = {
      text,
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top,
    };
  }

  hideTooltip(): void {
    if (this.tooltip.visible) this.tooltip.visible = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.hideTooltip();
  }

  onNumberKeyPress(event: KeyboardEvent): boolean {
    if (isNaN(event.key as any)) return false;
    this.otherService.insertComma(event);
    return true;
  }

  showHelp(): void {
    this.otherService.guid(HELP_GUIDE_ID);
  }

  goBack(): void {
    this.location.back();
  }

  // ================= کمکی‌ها =================
  private authQuery(): string {
    const session = encodeURIComponent(
      this.gurdService.decrypt(localStorage.getItem('Session')!),
    );
    const userId = this.gurdService.decrypt(localStorage.getItem('ID')!);
    return `iSystemUser=${userId}&strSession=${session}`;
  }

  private expireSession(errCode?: number): void {
    this.notifyService.showError('لطفا وارد سیستم شوید');
    this.otherService.removeLocalStorage();
    this.router.navigate(
      ['/login'],
      errCode ? { queryParams: { ERR_CODE: errCode } } : {},
    );
  }
}
